import { Check, Copy, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

import { GuideSteps, Eyebrow, NumberRow, type GuideStep } from './guideStepPrimitives';

export type { GuideStep };

const CODE_WINDOW_SEC = 180;

export interface BankRegistrationGuideProps {
  bankAppLabel: string;
  authCodeLength: number;
  authCode: string | null;
  codeRemainingSec: number | null;
  registered: boolean;
  requestCodeSteps: GuideStep[];
  completeRegistrationSteps: GuideStep[];
  notifyOptions: GuideStep[];
  awaitingCodeMessage: string;
  gatewayNumber: string;
  onCancel: () => void;
  onComplete: () => void;
}

function copy(text: string, label: string) {
  navigator.clipboard?.writeText(text).then(
    () => toast.success(`${label} 복사했어요`),
    () => toast.error('복사하지 못했어요'),
  );
}

export function BankRegistrationGuide({
  bankAppLabel,
  authCodeLength,
  authCode,
  codeRemainingSec,
  registered,
  requestCodeSteps,
  completeRegistrationSteps,
  notifyOptions,
  awaitingCodeMessage,
  gatewayNumber,
  onCancel,
  onComplete,
}: BankRegistrationGuideProps) {
  const hasCode = !!authCode;
  const isLongCode = authCodeLength > 4;
  const progressPercent = codeRemainingSec != null
    ? Math.max(0, Math.min(100, (codeRemainingSec / CODE_WINDOW_SEC) * 100))
    : 0;
  const isCodeExpiringSoon = codeRemainingSec != null && codeRemainingSec <= 30;

  const renderCodeCells = (characters: string[]) => (
    <div className={cn('mt-4 flex justify-center', isLongCode ? 'gap-1.5' : 'gap-2.5')} aria-hidden="true">
      {characters.map((character, index) => (
        <div
          key={index}
          className={cn(
            'flex items-center justify-center bg-white font-bold leading-none tabular-nums ring-1',
            isLongCode
              ? 'h-14 w-10 rounded-xl text-2xl'
              : 'h-16 w-[3.25rem] rounded-2xl text-[30px]',
            hasCode
              ? 'text-slate-900 shadow-sm ring-slate-200/70'
              : 'text-slate-200 ring-slate-200/60',
          )}
        >
          {character}
        </div>
      ))}
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <Eyebrow>은행 앱 ‘수신 휴대폰 번호’에 입력</Eyebrow>
        <NumberRow label="은행 앱에 입력할 번호" value={gatewayNumber} />
        <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs font-medium ring-1 ring-amber-100">
          <span className="block text-amber-800">
            내 휴대폰 번호를 입력하면 자동 매칭이 작동하지 않아요.
          </span>
          <span className="mt-1 block font-semibold text-rose-600">
            내 휴대폰 번호가 아니라 {gatewayNumber}를 입력해주세요.
          </span>
        </p>
      </div>

      <div className="space-y-3">
        <Eyebrow>{bankAppLabel}에서 따라하기</Eyebrow>
        <GuideSteps steps={requestCodeSteps} startAt={1} />
      </div>

      <div className="rounded-2xl border border-slate-100 bg-slate-50/60 px-5 py-6">
        <p className="text-center text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-400">
          인증요청 후 은행 앱에 입력할 인증번호
        </p>
        {registered && (
          <p className="mt-1.5 flex items-center justify-center gap-1 text-xs font-medium text-teal-700">
            <ShieldCheck className="h-3.5 w-3.5" /> 등록 확인됨
          </p>
        )}

        {hasCode ? (
          <>
            {renderCodeCells(authCode.split(''))}
            <span className="sr-only">인증번호 {authCode}</span>
            {codeRemainingSec != null && (
              <>
                <div className="mx-auto mt-5 h-1 max-w-[13rem] overflow-hidden rounded-full bg-slate-200/70">
                  <div
                    className={cn(
                      'h-full rounded-full transition-[width] duration-1000 ease-linear',
                      isCodeExpiringSoon ? 'bg-amber-500' : 'bg-teal-500',
                    )}
                    style={{ width: `${progressPercent}%` }}
                  />
                </div>
                <p className={cn(
                  'mt-2 text-center text-xs tabular-nums',
                  isCodeExpiringSoon ? 'text-amber-600' : 'text-slate-400',
                )}>
                  {codeRemainingSec}초 후 만료
                </p>
              </>
            )}
            <button
              type="button"
              onClick={() => copy(authCode, '인증번호')}
              className="mx-auto mt-3 flex cursor-pointer items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold text-teal-700 transition-colors hover:bg-teal-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500/40"
            >
              <Copy className="h-3.5 w-3.5" /> 인증번호 복사
            </button>
          </>
        ) : (
          <>
            {renderCodeCells(Array.from({ length: authCodeLength }, () => '–'))}
            <p className="mt-4 text-center text-sm text-slate-500">{awaitingCodeMessage}</p>
          </>
        )}
      </div>

      <div className="space-y-3">
        <Eyebrow>인증번호 입력 후 마무리하기</Eyebrow>
        <GuideSteps
          steps={completeRegistrationSteps}
          startAt={requestCodeSteps.length + 1}
        />
      </div>

      <div className="rounded-xl bg-slate-50 p-4 ring-1 ring-slate-100">
        <Eyebrow>통지 옵션</Eyebrow>
        <ul className="mt-2 space-y-1.5">
          {notifyOptions.map((option) => (
            <li key={option.title} className="flex gap-2 text-sm text-slate-600">
              <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-teal-600" />
              <span className={cn(
                option.titleTone === 'danger' && 'font-semibold text-rose-600',
              )}>
                {option.title}
                {option.detail && <span className="text-slate-400"> — {option.detail}</span>}
              </span>
            </li>
          ))}
        </ul>
      </div>

      <div className="space-y-2 border-t border-slate-100 pt-4">
        <div className="flex gap-2.5">
          <Button
            variant="outline"
            className="h-11 flex-1 rounded-xl border-slate-200 font-medium text-slate-600 hover:bg-slate-50"
            onClick={onCancel}
          >
            취소
          </Button>
          <Button
            className="h-11 flex-1 rounded-xl bg-teal-600 font-semibold text-white shadow-sm transition-colors hover:bg-teal-700 disabled:opacity-50"
            onClick={onComplete}
            disabled={!hasCode}
          >
            등록 완료했어요
          </Button>
        </div>
        {!hasCode && (
          <p className="text-center text-xs text-slate-400">
            인증번호가 도착하면 완료 버튼이 활성화돼요.
          </p>
        )}
      </div>
    </div>
  );
}
